import { useEffect, useState, type FC } from "react";
import SearchRestaurant from "../SearchRestaurant";
import type { RestaurantAutocompleteResult, RestaurantDetails } from "@/API";
import Text from "../Text";
import {
  getRestaurantToCompare,
  resetRestaurantToCompare,
  restaurantToCompareState,
} from "@/store/restaurantToCompareState";
import { Skeleton } from "../ui/skeleton";
import KrunshScore from "../KrunshScore";
import KrunshScoreCard from "../KrunshScoreCard";
import Badge from "../Badge";
import { BikeIcon, StarIcon } from "lucide-react";
import { Button } from "../ui/button";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

type Props = {
  restaurant: RestaurantDetails | null;
};

const RestaurantCompare: FC<Props> = ({ restaurant }) => {
  const { data: restaurantToCompare, isLoading } = restaurantToCompareState();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isExplorer = pathname.includes("/explorer");

  const [restaurantSelected, setRestaurantSelected] =
    useState<RestaurantAutocompleteResult | null>(null);

  useEffect(() => {
    return () => {
      resetRestaurantToCompare();
    };
  }, []);

  if (!restaurant) return null;

  const nearbyRestaurants = restaurant.nearbyRestaurants.restaurants.filter(
    (r) => r?.id !== restaurant.id,
  );

  return (
    <div>
      <SearchRestaurant
        onChange={(value) => {
          getRestaurantToCompare({ id: value.id });
          setRestaurantSelected(value);
        }}
      />

      {!restaurantSelected && (
        <div className="bg-[white] flex items-center justify-center h-[100px] rounded-[8px] mt-[24px]">
          <Text fontSize={14} fontWeight={400}>
            {t("select_restaurant_to_compare")}
          </Text>
        </div>
      )}

      {isLoading && (
        <div className="flex flex-col gap-[24px] mt-[24px]">
          <Skeleton className="h-[24px] w-[350px]" />
          <Skeleton className="h-[450px] w-full" />
        </div>
      )}

      {restaurantToCompare && (
        <div className="flex flex-col gap-[24px] mt-[24px]">
          <Text
            fontWeight={600}
            fontSize={20}
          >{`${restaurant.name} VS ${restaurantToCompare.name}`}</Text>
          <div className="bg-[white] p-[24px] rounded-[8px] gap-[16px]">
            <Text fontSize={14} fontWeight={600}>
              {t("key_metrics")}
            </Text>
            <div className="grid grid-cols-2 gap-x-[16px] mt-[16px]">
              <div className="flex flex-col gap-y-[12px]">
                <Text fontSize={12} fontWeight={600}>
                  {restaurant.name}
                </Text>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <KrunshScore value={restaurant.krunshScore} />
                  <Text fontSize={12} fontWeight={400}>
                    {t("krunsh_score")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {`${restaurant.rating}/5`}
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("average_rating")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {`${restaurant.ratingCount}`}
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("rating_count")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {`${restaurant.deliveryTime} min`}
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("delivery_time")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {restaurant.menuItems.length}
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("total_items")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {`${
                      (restaurant.menuItems.filter(
                        (item) => !item?.isSoldOut && item?.categoryName,
                      ).length /
                        restaurant.menuItems.length) *
                      100
                    }%`}
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("items_in_stock")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex gap-[8px] flex-wrap p-[16px] rounded-[12px]">
                  {restaurant.categories?.map((category) => (
                    <Badge key={`category-${category}`}>
                      <Text fontWeight={400} fontSize={14} color="#1D7AFC">
                        {category}
                      </Text>
                    </Badge>
                  ))}
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {
                      restaurant.menuItems.filter(
                        (item) => !item?.imageUrl && item?.categoryName,
                      ).length
                    }
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("images_missing")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {
                      restaurant.menuItems.filter(
                        (item) => !item?.description && item?.categoryName,
                      ).length
                    }
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("missing_description")}
                  </Text>
                </div>
              </div>

              <div className="flex flex-col gap-y-[12px]">
                <Text fontSize={12} fontWeight={600}>
                  {restaurantToCompare.name}
                </Text>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <KrunshScore value={restaurantToCompare.krunshScore} />
                  <Text fontSize={12} fontWeight={400}>
                    {t("krunsh_score")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {`${restaurantToCompare.rating}/5`}
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("average_rating")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {`${restaurantToCompare.ratingCount}`}
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("rating_count")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {`${restaurantToCompare.deliveryTime} min`}
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("delivery_time")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {restaurantToCompare.menuItems.length}
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("total_items")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {`${
                      (restaurantToCompare.menuItems.filter(
                        (item) => !item?.isSoldOut && item?.categoryName,
                      ).length /
                        restaurantToCompare.menuItems.length) *
                      100
                    }%`}
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("items_in_stock")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex gap-[8px] flex-wrap p-[16px] rounded-[12px]">
                  {restaurantToCompare.categories?.map((category) => (
                    <Badge key={`category-${category}`}>
                      <Text fontWeight={400} fontSize={14} color="#1D7AFC">
                        {category}
                      </Text>
                    </Badge>
                  ))}
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {
                      restaurantToCompare.menuItems.filter(
                        (item) => !item?.imageUrl && item?.categoryName,
                      ).length
                    }
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("images_missing")}
                  </Text>
                </div>
                <div className="bg-[#F7F8F9] w-full py-[16px] flex flex-col gap-[8px] items-center justify-center rounded-[12px]">
                  <Text fontSize={16} fontWeight={600}>
                    {
                      restaurantToCompare.menuItems.filter(
                        (item) => !item?.description && item?.categoryName,
                      ).length
                    }
                  </Text>
                  <Text fontSize={12} fontWeight={400}>
                    {t("missing_description")}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {restaurantToCompare && !isExplorer && (
        <div className="flex flex-col gap-[24px] mt-[24px]">
          <div className="bg-[white] p-[24px] rounded-[8px] gap-[16px]">
            <Text fontSize={14} fontWeight={600}>
              {t("krunsh_score")}
            </Text>
            <div className="grid grid-cols-2 gap-x-[16px] mt-[16px]">
              <KrunshScoreCard
                krunshScore={restaurant.krunshScore}
                krunshScoreDetails={restaurant.krunshScoreDetails}
              />

              <KrunshScoreCard
                krunshScore={restaurantToCompare.krunshScore}
                krunshScoreDetails={restaurantToCompare.krunshScoreDetails}
              />
            </div>
          </div>
        </div>
      )}

      {nearbyRestaurants.length > 0 && (
        <>
          <div className="my-[24px]">
            <Text fontSize={20} fontWeight={600}>
              {t("suggestions")}
            </Text>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[12px]">
            {nearbyRestaurants.map((nearbyRestaurant) => (
              <div className="bg-[white] rounded-[12px] px-[24px] py-[16px]">
                <div className="flex gap-[12px] items-center">
                  <img
                    className="h-[40px] w-[40px] object-cover rounded-[3px]"
                    src={nearbyRestaurant?.imageUrl ?? undefined}
                    alt={nearbyRestaurant?.name}
                  />
                  <div className="flex flex-col gap-[12px]">
                    <Text fontWeight={600} fontSize={14}>
                      {nearbyRestaurant?.name}
                    </Text>
                    <Text
                      fontWeight={400}
                      fontSize={12}
                      color="#535862"
                      className="truncate"
                    >
                      {nearbyRestaurant?.address}
                    </Text>
                  </div>
                </div>
                <div className="flex items-center gap-[8px] mt-[12px] mb-[24px]">
                  <img
                    width={24}
                    height={24}
                    className="rounded-[50%]"
                    src={`/partner/${nearbyRestaurant?.platform?.toLowerCase()}.png`}
                    alt={`logo of ${nearbyRestaurant?.platform}`}
                  />
                  <Badge>
                    {nearbyRestaurant?.rating === "0" ? (
                      <Text fontWeight={600} fontSize={14}>
                        {t("not_rated")}
                      </Text>
                    ) : (
                      <>
                        <StarIcon height={16} width={16} color="#758195" />
                        <Text fontWeight={600} fontSize={14}>
                          {nearbyRestaurant?.rating}
                        </Text>
                      </>
                    )}
                  </Badge>

                  <Badge>
                    <BikeIcon height={16} width={16} color="#758195" />
                    <Text fontWeight={600} fontSize={14}>
                      {`${nearbyRestaurant?.deliveryTime} min`}
                    </Text>
                  </Badge>
                </div>
                <Button
                  onClick={() => {
                    getRestaurantToCompare({
                      id: nearbyRestaurant?.id ?? "",
                    });
                    setRestaurantSelected(
                      nearbyRestaurant as unknown as RestaurantAutocompleteResult,
                    );
                  }}
                  variant="secondaryFilled"
                >
                  {t("compare")}
                </Button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default RestaurantCompare;
